import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from 'axios';
import AppURL from "./AppUrl";

const RendezVousModal = ({ language, isOpen, onClose }: { language: string; isOpen: boolean; onClose: () => void }) => {
    const location = useLocation();
    const [type, setType] = useState("Mécanique");
    const [formData, setFormData] = useState({
        type: "",
        clientFullName: "",
        clientPhoneNumber: "",
        clientEmail: "",
        date: "",
        heure: "",
        description: "",
        preferredLanguage: 'fr', // Nouveau champ
        contactMethod: 'email',   // Nouveau champ
    });
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [availableSlots, setAvailableSlots] = useState<string[]>([]);
    const [loadingSlots, setLoadingSlots] = useState(false);

    // Générer tous les créneaux possibles de 9h à 19h
    const allTimeSlots = Array.from({ length: 10 }, (_, i) => {
        const hour = 9 + i;
        return `${hour}:00`;
    });
    // Déterminer le type de rendez-vous (Mécanique ou Carrosserie)
    useEffect(() => {
        if (location.pathname.includes("services-mecanique")) {
            setType("Mécanique");
        } else if (location.pathname.includes("services-carrosserie")) {
            setType("Carrosserie");
        }
    }, [location.pathname]);

    // Charger les créneaux disponibles quand la date change
    useEffect(() => {
        if (formData.date) {
            fetchAvailableTimeSlots(formData.date);
        }
    }, [formData.date]);

    const fetchAvailableTimeSlots = async (date: string) => {
        setLoadingSlots(true);
        try {
            // Récupérer les rendez-vous pour la date sélectionnée
            const response = await axios.get(`${AppURL}/api/rendezvous/byDate/${date}`);
            const takenSlots = response.data.map((rdv: any) => rdv.heure);

            // Filtrer les créneaux disponibles
            const available = allTimeSlots.filter(slot => !takenSlots.includes(slot));
            setAvailableSlots(available);
        } catch (error) {
            console.error("Erreur lors de la récupération des créneaux:", error);
            setAvailableSlots(allTimeSlots); // En cas d'erreur, afficher tous les créneaux
        } finally {
            setLoadingSlots(false);
        }
    };

    // Gérer les champs texte
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const requiredFields = [
            formData.clientFullName,
            formData.clientPhoneNumber,
            formData.clientEmail,
            formData.date,
            formData.heure,
            formData.description,
            formData.preferredLanguage,
            formData.contactMethod,
        ];

        if (requiredFields.some(field => !field)) {
            setError(language === "fr"
                ? "Tous les champs sont obligatoires."
                : "All fields are required.");
            return false;
        }

        setError("");
        return true;
    };

    const successMessages = {
        fr: "Votre demande de rendez-vous a été bien reçue. Nous vous contacterons pour confirmation.",
        en: "Your appointment request has been received. We will contact you for confirmation.",
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            setIsSubmitting(true);

            try {
                const response = await axios.post(
                    `${AppURL}/api/rendezvous/create`,
                    {
                        ...formData,
                        type: type
                    }
                );

                if (response.data.success) {
                    const notification = document.createElement('div');
                    notification.className = `
                        fixed top-4 right-4 z-50
                        bg-white border-l-4 border-green-500
                        shadow-lg rounded-lg overflow-hidden
                        w-full max-w-xs
                        transform transition-all duration-300
                        opacity-0 translate-y-2
                    `;

                    notification.innerHTML = `
                        <div class="flex items-start p-4">
                            <div class="flex-shrink-0">
                                <svg class="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div class="ml-3 w-0 flex-1 pt-0.5">
                                <p class="text-sm font-medium text-gray-900">
                                    ${language === 'fr' ? 'Succès!' : 'Success!'}
                                </p>
                                <p class="mt-1 text-sm text-gray-500">
                                    ${successMessages[language as keyof typeof successMessages] || successMessages['fr']}
                                </p>
                            </div>
                            <div class="ml-4 flex-shrink-0 flex">
                                <button class="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none">
                                    <span class="sr-only">Fermer</span>
                                    <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 text-right">
                            <button type="button" class="text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none">
                                ${language === 'fr' ? 'Fermer' : 'Close'}
                            </button>
                        </div>
                    `;

                    document.body.appendChild(notification);

                    setTimeout(() => {
                        notification.classList.remove('opacity-0', 'translate-y-2');
                        notification.classList.add('opacity-100', 'translate-y-0');
                    }, 10);

                    const closeBtn = notification.querySelector('button');
                    closeBtn?.addEventListener('click', () => {
                        notification.classList.add('opacity-0', 'translate-y-2');
                        setTimeout(() => {
                            notification.remove();
                            onClose();
                        }, 300);
                    });

                    setTimeout(() => {
                        notification.classList.add('opacity-0', 'translate-y-2');
                        setTimeout(() => {
                            notification.remove();
                            onClose();
                        }, 300);
                    }, 5000);

                } else {
                    console.warn('Erreur :', response.data.message);
                }
            } catch (error: any) {
                console.error('Erreur lors de l\'envoi :', error.response?.data || error.message);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="mt-20 fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
            {isSubmitting && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                        <p className="text-gray-700">
                            {language === 'fr' ? 'Envoi en cours...' : 'Submitting...'}
                        </p>
                    </div>
                </div>
            )}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white w-full max-w-3xl mx-4 p-6 rounded-lg shadow-lg overflow-y-auto max-h-[80vh]">
                <h2 className="text-3xl font-extrabold text-yellow-500 mb-4">
                    {language === "fr"
                        ? type === "Mécanique"
                            ? "Rendez-vous Mécanique"
                            : "Rendez-vous Carrosserie"
                        : type === "Mécanique"
                            ? "Mechanical Appointment"
                            : "Bodywork Appointment"}
                </h2>

                <form>
                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Nom complet" : "Full Name"}</label>
                        <input
                            type="text"
                            name="clientFullName"
                            value={formData.clientFullName}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Telephone" : "Phone"}</label>
                        <input
                            type="text"
                            name="clientPhoneNumber"
                            value={formData.clientPhoneNumber}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            required
                        />
                    </div>


                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Email" : "Email"}</label>
                        <input
                            type="text"
                            name="clientEmail"
                            value={formData.clientEmail}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Date" : "Date"}</label>
                        <input
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            min={new Date().toISOString().split("T")[0]} // <- limite minimum à aujourd'hui
                            required
                        />
                    </div>


                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Heure" : "Time"}</label>
                        {loadingSlots ? (
                            <div className="flex items-center justify-center p-2">
                                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-yellow-500"></div>
                                <span className="ml-2">
                                    {language === "fr" ? "Chargement des créneaux..." : "Loading time slots..."}
                                </span>
                            </div>
                        ) : (
                            <select
                                name="heure"
                                value={formData.heure}
                                onChange={handleSelectChange}
                                className="w-full p-2 border rounded bg-gray-800 text-white"
                                required
                                disabled={!formData.date || loadingSlots}
                            >
                                <option value="">{language === "fr" ? "Sélectionnez une heure" : "Select a time"}</option>
                                {availableSlots.map(time => (
                                    <option key={time} value={time}>{time}</option>
                                ))}
                            </select>
                        )}
                        {!formData.date && (
                            <p className="text-sm text-gray-400 mt-1">
                                {language === "fr"
                                    ? "Veuillez d'abord sélectionner une date"
                                    : "Please select a date first"}
                            </p>
                        )}
                    </div>

                    {/* Nouveau champ : Langue de communication préférée */}
                    <div className="mb-4">
                        <label className="block text-yellow-500">
                            {language === 'fr' ? 'Langue de communication préférée' : 'Preferred communication language'}
                        </label>
                        <select
                            id="preferredLanguage"
                            name="preferredLanguage"
                            value={formData.preferredLanguage}
                            onChange={handleSelectChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white">

                            <option value="fr">Français</option>
                            <option value="en">English</option>
                        </select>
                    </div>


                    {/* Nouveau champ : Méthode de contact préférée */}
                    <div className="mb-4">
                        <label className="block text-yellow-500">
                            {language === 'fr' ? 'Méthode de contact préférée' : 'Preferred contact method'}
                        </label>
                        <select
                            id="contactMethod"
                            name="contactMethod"
                            value={formData.contactMethod}
                            onChange={handleSelectChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white">

                            <option value="email">Email</option>
                            <option value="phone">{language === 'fr' ? 'Téléphone' : 'Phone'}</option>
                            <option value="both">{language === 'fr' ? 'Les deux' : 'Both'}</option>
                        </select>
                    </div>

                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Description du problème" : "Problem Description"}</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            required
                        ></textarea>
                    </div>

                    {error && <p className="text-red-500">{error}</p>}

                    <div className="flex justify-end space-x-4 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 transition duration-300"
                        >
                            {language === "fr" ? "Annuler" : "Cancel"}
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded hover:bg-yellow-600 transition duration-300"
                        >
                            {language === "fr" ? "Soumettre" : "Submit"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RendezVousModal;